import type { PropsWithChildren } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { queryRetryDelay, shouldRetryQuery } from "./queryRetryPolicy.js";

/**
 * Audit C-4 (docs/plans/error-empty-loading-audit-2026-05-20.md).
 *
 * Smarter retry policy lives in queryRetryPolicy.ts (so this file remains
 * component-only for react-refresh). Summary:
 *   - 4xx (incl. 401/403/404) → never retry.
 *   - 5xx + network failures → retry up to 2 times with exp backoff capped
 *     at 30s.
 *   - Mutations stay at retry: 0 (TanStack default). They have side-effects
 *     and the UI surfaces manual retry via showToast / ErrorState instead.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
      retryDelay: queryRetryDelay,
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
    },
  },
});

export function QueryProvider({ children }: PropsWithChildren) {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
